/**
 * One shared subscription to "web fonts finished loading", however many
 * components listen: a single `loadingdone` listener and `fonts.ready`
 * continuation on `document.fonts`, fanned out to every subscriber.
 */
const subscribers = new Set<() => void>();
let wiredTo: FontFaceSet | null = null;
// `fonts.ready` resolves once and cannot be detached from, so it is chained at
// most once per FontFaceSet; re-wiring after an unwire must not add a second.
const readyChained = new WeakSet<FontFaceSet>();

function notify() {
  subscribers.forEach((fn) => {
    // One row's failed refit must not stop the rest from refitting.
    try {
      fn();
    } catch (error) {
      console.error("onFontsLoaded subscriber failed", error);
    }
  });
}

function wire() {
  const fonts = typeof document !== "undefined" ? document.fonts : undefined;
  if (!fonts || fonts === wiredTo) return;
  wiredTo?.removeEventListener?.("loadingdone", notify);
  wiredTo = fonts;
  fonts.addEventListener?.("loadingdone", notify);
  if (readyChained.has(fonts)) return;
  readyChained.add(fonts);
  // Notifies whoever is subscribed when it lands, wired or not: an unwire in
  // between must not swallow it for subscribers that arrive afterwards.
  fonts.ready
    ?.then(notify)
    // A font that fails to load leaves the fallback in place: nothing to refit.
    .catch(() => undefined);
}

function unwire() {
  wiredTo?.removeEventListener?.("loadingdone", notify);
  wiredTo = null;
}

/** Calls `fn` whenever web fonts finish loading. Returns the unsubscribe. */
export function onFontsLoaded(fn: () => void): () => void {
  wire();
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
    if (subscribers.size === 0) unwire();
  };
}
